import React, { useMemo, useState } from 'react';

// Import bookmaker logos
import sportsbetLogo from '../../assets/sb.png';
import tabLogo from '../../assets/tab.jpg';
import pointsbetLogo from '../../assets/pb.png';
import dabbleLogo from '../../assets/dabble.webp';
import nedsLogo from '../../assets/neds.jpg';
import betrightLogo from '../../assets/betright.jpg';
import tabtouchLogo from '../../assets/tabtouch.webp';
import bet365Logo from '../../assets/bet365.png';
import betmakersLogo from '../../assets/betmakers.png';
import ladbrokesLogo from '../../assets/ladbrokes.webp';
import betrLogo from '../../assets/betr.jpg';
import picklebetLogo from '../../assets/picklebet.jpeg';
import playupLogo from '../../assets/playup.png';
import unibetLogo from '../../assets/unibet.webp';

function BookmakerBreakdown({ bets, displayMode = 'dollars', unitSize = 25, getUnitSizeForDate, advancedUnitSettings = false }) {
    const [sortBy, setSortBy] = useState('pl');
    const isUnits = displayMode === 'units';

    const bookmakerGroups = {
        'Ladbrokes': 'Ladbrokes/Neds',
        'Neds': 'Ladbrokes/Neds',
    };

    const bookieLogos = {
        'Sportsbet': sportsbetLogo,
        'TAB': tabLogo,
        'Pointsbet': pointsbetLogo,
        'Dabble': dabbleLogo,
        'Ladbrokes/Neds': ladbrokesLogo,
        'Neds': nedsLogo,
        'Betright': betrightLogo,
        'TABtouch': tabtouchLogo,
        'Bet365': bet365Logo,
        'bet365': bet365Logo,
        'Betmakers': betmakersLogo,
        'Betr': betrLogo,
        'Picklebet': picklebetLogo,
        'PlayUp': playupLogo,
        'Unibet': unibetLogo,
    };

    const bookmakerData = useMemo(() => {
        const grouped = {};

        bets.forEach(bet => {
            const rawBookie = bet.bookie || 'Unknown';
            const bookie = bookmakerGroups[rawBookie] || rawBookie;
            const divisor = isUnits && advancedUnitSettings && getUnitSizeForDate ? getUnitSizeForDate(bet.date) : (isUnits ? unitSize : 1);
            
            if (!grouped[bookie]) {
                grouped[bookie] = {
                    name: bookie,
                    bets: 0,
                    wins: 0,
                    losses: 0,
                    totalStaked: 0,
                    totalReturned: 0,
                    pl: 0
                };
            }

            grouped[bookie].bets++;
            if (!bet.bonus_bet) {
                grouped[bookie].totalStaked += bet.stake / divisor;
            }
            grouped[bookie].pl += bet.pl / divisor;
            
            if (bet.result !== 'pending') {
                grouped[bookie].totalReturned += ((bet.bonus_bet ? 0 : bet.stake) + bet.pl) / divisor;
            }

            if (bet.result === 'win') grouped[bookie].wins++;
            if (bet.result === 'loss') grouped[bookie].losses++;
        });

        return Object.values(grouped)
            .map(b => ({
                ...b,
                winRate: b.bets > 0 ? (b.wins / (b.wins + b.losses)) * 100 : 0,
                roi: b.totalStaked > 0 ? (b.pl / b.totalStaked) * 100 : 0
            }))
            .sort((a, b) => {
                if (sortBy === 'pl') return b.pl - a.pl;
                if (sortBy === 'roi') return b.roi - a.roi;
                return 0;
            });
    }, [bets, sortBy, isUnits, unitSize, advancedUnitSettings, getUnitSizeForDate]);

    // Data sorted by P/L for the chart (always)
    const chartData = useMemo(() => {
        return [...bookmakerData].sort((a, b) => b.pl - a.pl);
    }, [bookmakerData]);

    const maxAbsPL = Math.max(...bookmakerData.map(b => Math.abs(b.pl)), 1);
    const totalPL = bookmakerData.reduce((sum, b) => sum + b.pl, 0);

    const formatCurrency = (value) => {
        const prefix = value >= 0 ? '+' : '';
        if (isUnits) return `${prefix}${value.toFixed(1)}U`;
        return `${prefix}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    };

    const formatCurrencyFull = (value) => {
        const prefix = value >= 0 ? '+' : '';
        if (isUnits) return `${prefix}${value.toFixed(1)}U`;
        return `${prefix}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatStaked = (value) => {
        if (isUnits) return `${value.toFixed(1)}U`;
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Bookmaker colors
    const getBookieColor = (name) => {
        const colors = {
            'Sportsbet': '#f97316',
            'TAB': '#3b82f6',
            'Pointsbet': '#8b5cf6',
            'Dabble': '#ec4899',
            'Ladbrokes/Neds': '#e11d48',
            'Neds': '#ef4444',
            'Betright': '#22c55e',
            'TABtouch': '#06b6d4',
            'bet365': '#84cc16',
            'Bet365': '#84cc16',
            'Ladbrokes': '#e11d48',
            'Betmakers': '#0ea5e9',
            'Other': '#64748b'
        };
        return colors[name] || '#64748b';
    };

    return (
        <div className="bg-[#0f172a] rounded border border-slate-700/50 overflow-hidden flex flex-col h-full">
            {/* Command Bar Header */}
            <div className="flex items-center justify-between px-3 md:px-4 py-2.5 border-b border-slate-700/50 bg-slate-800/30">
                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <div className={`w-1 h-4 ${totalPL >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    <h3 className="text-xs md:text-sm font-semibold text-white tracking-tight whitespace-nowrap">Bookmaker Performance</h3>
                    <span className="hidden md:inline text-[10px] font-mono text-slate-600 tracking-widest uppercase">{bookmakerData.length} books</span>
                </div>
                <div className={`text-base md:text-lg font-bold font-mono tabular-nums ${totalPL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatCurrencyFull(totalPL)}
                </div>
            </div>

            <div className="p-3 md:p-4 flex-1 flex flex-col">
                {/* P/L by Bookmaker - Horizontal Bar Chart */}
                <div className="mb-4">
                    <div className="flex items-center gap-1.5 mb-3">
                        <div className="w-1 h-1 rounded-full bg-emerald-500" />
                        <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase">P/L Distribution</div>
                    </div>
                    <div className="space-y-1.5">
                        {chartData.map((bookie) => {
                            const barWidth = (Math.abs(bookie.pl) / maxAbsPL) * 50;
                            const isPositive = bookie.pl >= 0;

                            return (
                                <div key={bookie.name} className="flex items-center gap-2 group" title={bookie.name}>
                                    <div className="w-7 flex items-center justify-center">
                                        {bookieLogos[bookie.name] ? (
                                            <img
                                                src={bookieLogos[bookie.name]}
                                                alt={bookie.name}
                                                className="w-5 h-5 rounded object-cover"
                                            />
                                        ) : (
                                            <div
                                                className="w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold text-white"
                                                style={{ backgroundColor: getBookieColor(bookie.name) }}
                                            >
                                                {bookie.name.charAt(0)}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex-1 flex items-center">
                                        <div className="w-1/2 flex justify-end">
                                            {!isPositive && (
                                                <div
                                                    className="h-4 bg-red-500/70 group-hover:bg-red-500 transition-all"
                                                    style={{ width: `${barWidth}%` }}
                                                />
                                            )}
                                        </div>
                                        <div className="w-px h-5 bg-slate-700" />
                                        <div className="w-1/2 flex justify-start">
                                            {isPositive && (
                                                <div
                                                    className="h-4 bg-emerald-500/70 group-hover:bg-emerald-500 transition-all"
                                                    style={{ width: `${barWidth}%` }}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className="w-16 text-right">
                                        <span className={`text-[11px] font-mono font-semibold tabular-nums ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {formatCurrency(bookie.pl)}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Performance by Bookmaker - Sortable Table */}
                <div className="border-t border-slate-700/50 pt-3 flex-1 flex flex-col min-h-0">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                            <div className="w-1 h-1 rounded-full bg-emerald-500" />
                            <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase">Performance</div>
                        </div>
                        <div className="text-[9px] font-mono text-slate-600 tracking-widest uppercase">
                            Click columns to sort
                        </div>
                    </div>

                    {bookmakerData.length === 0 ? (
                        <div className="text-center py-8 text-[11px] font-mono text-slate-600">
                            No bookmaker data available
                        </div>
                    ) : (
                        <div className="-mx-3 md:-mx-4 overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-800/40 border-y border-slate-700/40">
                                    <tr>
                                        {[
                                            { key: 'name', label: 'Bookmaker', align: 'left', sortable: false, mobile: true },
                                            { key: 'bets', label: 'Bets', align: 'right', sortable: false, mobile: true },
                                            { key: 'record', label: 'W · L', align: 'center', sortable: false, mobile: false },
                                            { key: 'winRate', label: 'Win %', align: 'left', sortable: false, mobile: false },
                                            { key: 'staked', label: 'Staked', align: 'right', sortable: false, mobile: false },
                                            { key: 'returned', label: 'Returned', align: 'right', sortable: false, mobile: false },
                                            { key: 'roi', label: 'ROI', align: 'right', sortable: true, mobile: true },
                                            { key: 'pl', label: 'P/L', align: 'right', sortable: true, mobile: true },
                                        ].map(col => {
                                            const isActive = sortBy === col.key;
                                            const alignCls = col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';
                                            const visibilityCls = col.mobile ? '' : 'hidden md:table-cell';
                                            const baseCls = `px-1.5 md:px-2 py-1.5 text-[9px] font-mono font-semibold tracking-widest uppercase ${alignCls} ${visibilityCls}`;
                                            return col.sortable ? (
                                                <th
                                                    key={col.key}
                                                    onClick={() => setSortBy(col.key)}
                                                    className={`${baseCls} cursor-pointer transition-colors ${
                                                        isActive ? 'text-emerald-400' : 'text-slate-500 hover:text-slate-300'
                                                    }`}
                                                >
                                                    <span className="inline-flex items-center gap-1">
                                                        {col.label}
                                                        {isActive && (
                                                            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 12 12">
                                                                <path d="M6 8L2 4h8z" />
                                                            </svg>
                                                        )}
                                                    </span>
                                                </th>
                                            ) : (
                                                <th key={col.key} className={`${baseCls} text-slate-500`}>{col.label}</th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/30">
                                    {bookmakerData.map((bookie) => {
                                        const winRateClamped = Math.min(Math.max(bookie.winRate, 0), 100);
                                        return (
                                            <tr
                                                key={bookie.name}
                                                className="hover:bg-slate-800/40 transition-colors"
                                            >
                                                {/* Bookmaker */}
                                                <td className="px-1.5 md:px-2 py-2">
                                                    <div className="flex items-center gap-1.5 md:gap-2 min-w-0">
                                                        {bookieLogos[bookie.name] ? (
                                                            <img
                                                                src={bookieLogos[bookie.name]}
                                                                alt={bookie.name}
                                                                className="w-5 h-5 rounded object-cover flex-shrink-0"
                                                            />
                                                        ) : (
                                                            <div
                                                                className="w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                                                                style={{ backgroundColor: getBookieColor(bookie.name) }}
                                                            >
                                                                {bookie.name.charAt(0)}
                                                            </div>
                                                        )}
                                                        <span className="text-[11px] text-white font-medium truncate">{bookie.name}</span>
                                                    </div>
                                                </td>
                                                {/* Bets */}
                                                <td className="px-1.5 md:px-2 py-2 text-right text-[11px] font-mono text-slate-300 tabular-nums">
                                                    {bookie.bets}
                                                </td>
                                                {/* Record */}
                                                <td className="hidden md:table-cell px-2 py-2 text-center text-[11px] font-mono tabular-nums">
                                                    <span className="text-emerald-400">{bookie.wins}</span>
                                                    <span className="text-slate-600 mx-0.5">·</span>
                                                    <span className="text-red-400">{bookie.losses}</span>
                                                </td>
                                                {/* Win % bar */}
                                                <td className="hidden md:table-cell px-2 py-2">
                                                    <div className="flex items-center gap-1.5 min-w-[90px]">
                                                        <div className="flex-1 h-1 bg-slate-700/50 rounded-sm overflow-hidden">
                                                            <div
                                                                className={`h-full ${winRateClamped >= 50 ? 'bg-emerald-500/70' : 'bg-amber-500/70'}`}
                                                                style={{ width: `${winRateClamped}%` }}
                                                            />
                                                        </div>
                                                        <span className="text-[10px] font-mono text-slate-300 tabular-nums w-9 text-right">
                                                            {bookie.winRate.toFixed(1)}%
                                                        </span>
                                                    </div>
                                                </td>
                                                {/* Staked */}
                                                <td className="hidden md:table-cell px-2 py-2 text-right text-[10px] font-mono text-slate-400 tabular-nums">
                                                    {formatStaked(bookie.totalStaked)}
                                                </td>
                                                {/* Returned */}
                                                <td className="hidden md:table-cell px-2 py-2 text-right text-[10px] font-mono text-slate-400 tabular-nums">
                                                    {formatStaked(bookie.totalReturned)}
                                                </td>
                                                {/* ROI chip */}
                                                <td className="px-1.5 md:px-2 py-2 text-right">
                                                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold tabular-nums ${
                                                        bookie.roi >= 0
                                                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                                            : 'bg-red-500/10 text-red-400 border border-red-500/20'
                                                    }`}>
                                                        {bookie.roi >= 0 ? '+' : ''}{bookie.roi.toFixed(1)}%
                                                    </span>
                                                </td>
                                                {/* P/L */}
                                                <td className={`px-1.5 md:px-2 py-2 text-right text-[11px] md:text-[12px] font-mono font-bold tabular-nums whitespace-nowrap ${
                                                    bookie.pl >= 0 ? 'text-emerald-400' : 'text-red-400'
                                                }`}>
                                                    {formatCurrencyFull(bookie.pl)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default BookmakerBreakdown;
